/**
 * Loadboard Connection APIs
 * Minimal client calls used by load-hub to manage ITF and PLS loadboard connections.
 */

import { loadsIngestServiceAxios } from 'setup/SetupAxios'
import type { LoadHubLoadboardStatusEntry } from '@numeo/types'

interface LoadboardConnectionAttemptPayload {
    loadboard: string
    connectionAttemptId: string
    connectionFlowId?: string
    connectionMethod: string
    connectionIntent: string
    attemptStartedAt: string
}

export const registerLoadboardConnectionAttempt = async (payload: LoadboardConnectionAttemptPayload): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/connection-attempts', payload, { timeout: 5_000 })
}

export const resolveLoadboardConnectionAttempt = async (
    attemptId: string,
    payload: { status: 'succeeded' | 'failed' | 'cancelled' | 'abandoned'; lastStep?: string; errorCode?: string }
): Promise<void> => {
    await loadsIngestServiceAxios.post(`/loads/loadboards/connection-attempts/${encodeURIComponent(attemptId)}/resolve`, payload, { timeout: 5_000 })
}

/**
 * Records the successful end of an explicit user connection attempt. Token refreshes
 * and extension background captures intentionally never call this endpoint.
 */
export const recordSuccessfulLoadboardConnection = async (
    loadboard: string,
    attempt: { attemptStartedAt: number; connectionAttemptId: string; connectionFlowId?: string; connectionMethod: string }
): Promise<number | undefined> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: { connectionSequence?: number } }>(
        `/loads/loadboards/${encodeURIComponent(loadboard)}/connection-success`,
        attempt,
        // A stalled best-effort history request must not leave the connection attempt without
        // its terminal analytics event; the caller falls back to the status-derived sequence.
        { timeout: 10_000 }
    )
    return response.data.data.connectionSequence
}

// ─── ITF ────────────────────────────────────────────────────────────────────

interface ItfIntegration extends Partial<LoadHubLoadboardStatusEntry> {
    email: string | null
}

export const connectItf = async (email: string, password: string): Promise<ItfIntegration> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: { email: string; isConnected: boolean } }>('/loads/loadboards/itf/connect', {
        email,
        password,
    })
    return { email: response.data.data.email }
}

export const getItfStatus = async (): Promise<ItfIntegration> => {
    const response = await loadsIngestServiceAxios.get<{ success: boolean; data: ItfIntegration & { isConnected: boolean } }>('/loads/loadboards/itf/status')
    return response.data.data
}

interface ItfContactInfo {
    firstName: string
    lastName: string
    emailAddress: string
    officePhone: string
    officePhoneExtension: string
}

export const getItfContactInfo = async (userId: number): Promise<ItfContactInfo> => {
    const response = await loadsIngestServiceAxios.get<{ success: boolean; data: ItfContactInfo }>('/loads/loadboards/itf/contact-info', { params: { userId } })
    return response.data.data
}

// ─── PLS ────────────────────────────────────────────────────────────────────

interface PlsIntegration extends Partial<LoadHubLoadboardStatusEntry> {
    id?: string
    username: string | null
    carrierOrgId?: string
    isConnected: boolean
}

export const connectPls = async (username: string, password: string): Promise<PlsIntegration> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: PlsIntegration }>('/loads/loadboards/pls/connect', { username, password })
    return response.data.data
}

export const getPlsStatus = async (): Promise<PlsIntegration> => {
    const response = await loadsIngestServiceAxios.get<{ success: boolean; data: PlsIntegration }>('/loads/loadboards/pls/status')
    return response.data.data
}

// ─── BEON ────────────────────────────────────────────────────────────────────

interface BeonLoginResult {
    requiresOtp: boolean
    sessionId?: string
}

/** Start BEON credential login. Returns either SMS OTP challenge or direct success. */
export const beonLoginCredentials = async (username: string, password: string): Promise<BeonLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: { requiresOtp?: boolean; sessionId?: string; isConnected?: boolean } }>(
        '/loads/loadboards/beon/login-credentials',
        { username, password }
    )
    return {
        requiresOtp: response.data.data.requiresOtp ?? false,
        sessionId: response.data.data.sessionId,
    }
}

/** Verify BEON SMS OTP code and complete login. */
export const beonVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/beon/verify-otp', { sessionId, code })
}

// ─── Navisphere ───────────────────────────────────────────────────────────────

interface NavisphereLoginResult {
    requiresOtp: boolean
    sessionId?: string
}

/** Start Navisphere credential login. Returns either OTP challenge or direct success. */
export const navisphereLoginCredentials = async (username: string, password: string): Promise<NavisphereLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: { requiresOtp?: boolean; sessionId?: string; isConnected?: boolean } }>(
        '/loads/loadboards/navisphere/login-credentials',
        { username, password }
    )
    return {
        requiresOtp: response.data.data.requiresOtp ?? false,
        sessionId: response.data.data.sessionId,
    }
}

/** Verify Navisphere email OTP code and complete login. */
export const navisphereVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/navisphere/verify-otp', { sessionId, code })
}

// ─── TQL ────────────────────────────────────────────────────────────────────

interface TqlLoginResult {
    requiresOtp: boolean
    sessionId?: string
    carrierId?: string
}

/**
 * Start TQL credential-based login. Returns either:
 * - { requiresOtp: false, carrierId } — login complete, no OTP needed
 * - { requiresOtp: true, sessionId } — SMS sent, call tqlVerifyOtp next
 */
export const tqlLoginCredentials = async (email: string, password: string): Promise<TqlLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{
        success: boolean
        data: { requiresOtp?: boolean; sessionId?: string; carrierId?: string; isConnected?: boolean }
    }>('/loads/loadboards/tql/login-credentials', { email, password })
    return {
        requiresOtp: response.data.data.requiresOtp ?? false,
        sessionId: response.data.data.sessionId,
        carrierId: response.data.data.carrierId,
    }
}

/** Verify TQL SMS OTP code and complete login. */
export const tqlVerifyOtp = async (sessionId: string, code: string): Promise<{ carrierId: string }> => {
    const response = await loadsIngestServiceAxios.post<{
        success: boolean
        data: { carrierId: string; isConnected: boolean }
    }>('/loads/loadboards/tql/verify-otp', { sessionId, code })
    return { carrierId: response.data.data.carrierId }
}

// ─── Emerge ──────────────────────────────────────────────────────────────────

/** Credential login for Emerge Market (Auth0 Universal Login). No OTP step. */
export const emergeLoginCredentials = async (email: string, password: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/emerge/login-credentials', { email, password })
}

// ─── Echo ─────────────────────────────────────────────────────────────────────

interface EchoLoginResult {
    requiresOtp: boolean
    sessionId?: string
}

/** Start Echo credential login. Returns OTP session if SMS MFA is required. */
export const echoLoginCredentials = async (email: string, password: string): Promise<EchoLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: { requiresOtp: boolean; sessionId?: string } }>(
        '/loads/loadboards/echo/login-credentials',
        { email, password }
    )
    return response.data.data
}

/** Verify Echo SMS OTP code and complete login. */
export const echoVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/echo/verify-otp', { sessionId, code })
}

// ─── Mode Global ──────────────────────────────────────────────────────────────

/** Credential login for Mode Global (Auth0 implicit flow). No OTP step. */
export const modeGlobalLoginCredentials = async (email: string, password: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/mode-global/login-credentials', { email, password })
}

// ─── KNX ──────────────────────────────────────────────────────────────────────

/** Credential login for KNX (Keycloak OIDC PKCE flow). No OTP step. */
export const knxLoginCredentials = async (email: string, password: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/knx/login-credentials', { email, password })
}

// ─── Convoy ───────────────────────────────────────────────────────────────────

interface ConvoyLoginResult {
    requiresOtp: true
    sessionId: string
}

/** Step 1: request SMS OTP to phone number. Returns sessionId for OTP verification. */
export const convoyLoginCredentials = async (phoneNumber: string): Promise<ConvoyLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: ConvoyLoginResult }>('/loads/loadboards/convoy/login-credentials', {
        phoneNumber,
    })
    return response.data.data
}

/** Step 2: verify SMS OTP code. On success, integration is saved. */
export const convoyVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/convoy/verify-otp', { sessionId, code })
}

// ─── Spot ─────────────────────────────────────────────────────────────────────

interface SpotLoginResult {
    requiresOtp: true
    sessionId: string
}

/** Step 1: request SMS OTP to phone number. Returns sessionId for OTP verification. */
export const spotLoginCredentials = async (phone: string): Promise<SpotLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: SpotLoginResult }>('/loads/loadboards/spot/login-credentials', { phone })
    return response.data.data
}

/** Step 2: verify SMS OTP code. On success, integration is saved. */
export const spotVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/spot/verify-otp', { sessionId, code })
}

// ─── RXO ──────────────────────────────────────────────────────────────────────

interface RxoLoginResult {
    requiresOtp: true
    sessionId: string
}

/** Step 1: submit email + password. Returns sessionId for OTP verification. */
export const rxoLoginCredentials = async (email: string, password: string): Promise<RxoLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: RxoLoginResult }>('/loads/loadboards/rxo/login-credentials', {
        email,
        password,
    })
    return response.data.data
}

/** Step 2: verify SMS OTP code. On success, integration is saved. */
export const rxoVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/rxo/verify-otp', { sessionId, code })
}

// ─── Arrive ───────────────────────────────────────────────────────────────────

export type ArriveOtpMethod = 'sms' | 'totp'

interface ArriveLoginResult {
    requiresOtp: true
    sessionId: string
    /** Factor Okta auto-selected for this account — used by the UI to label the OTP step. */
    method: ArriveOtpMethod
}

/**
 * Step 1: start Arrive credential login with email + password.
 * The MFA factor is detected server-side from the Okta identify response and returned
 * as `method`. Always returns an OTP challenge.
 */
export const arriveLoginCredentials = async (email: string, password: string): Promise<ArriveLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: ArriveLoginResult }>('/loads/loadboards/arrive/login-credentials', {
        email,
        password,
    })
    return response.data.data
}

/** Step 2: verify Arrive OTP code (SMS or authenticator app). On success, integration is saved. */
export const arriveVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/arrive/verify-otp', { sessionId, code })
}

// ─── Schneider ────────────────────────────────────────────────────────────────

interface SchneiderLoginResult {
    requiresOtp: true
    sessionId: string
}

/** Step 1: start Schneider credential login with email + password. Always returns OTP challenge. */
export const schneiderLoginCredentials = async (email: string, password: string): Promise<SchneiderLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: SchneiderLoginResult }>('/loads/loadboards/schneider/login-credentials', {
        email,
        password,
    })
    return response.data.data
}

/** Step 2: verify Schneider SMS OTP code. On success, integration is saved. */
export const schneiderVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/schneider/verify-otp', { sessionId, code })
}

// ─── TruckStop Account (credential login) ─────────────────────────────────────

interface TruckstopAccountLoginResult {
    requiresOtp: true
    sessionId: string
}

/** Step 1: start TruckStop Account credential login with email + password. Always returns OTP challenge. */
export const truckstopAccountLoginCredentials = async (email: string, password: string): Promise<TruckstopAccountLoginResult> => {
    const response = await loadsIngestServiceAxios.post<{ success: boolean; data: TruckstopAccountLoginResult }>(
        '/loads/loadboards/truckstop-account/login-credentials',
        {
            email,
            password,
        }
    )
    return response.data.data
}

/** Step 2: verify TruckStop OTP code. On success, integration is saved to Firestore. */
export const truckstopAccountVerifyOtp = async (sessionId: string, code: string): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/loadboards/truckstop-account/verify-otp', { sessionId, code })
}
